import React, { useMemo } from 'react';
import { Alert, Button, Empty, Image, List, Space, Spin, Typography } from 'antd';
import { DownloadOutlined, FileImageOutlined, FolderOutlined } from '@ant-design/icons';
import { useMeasurementReport } from './measurementReport/MeasurementReportContext';
import { MeasurementSectionShell, buildMeasurementSectionMeta } from './measurementReport/MeasurementSectionShell';

const { Text } = Typography;

interface CommonSectionProps {
    section: any;
}

const formatFileSize = (size?: number) => {
    if (size === undefined || size === null || !Number.isFinite(size)) return null;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

export const ReportMeasurementFiles: React.FC<CommonSectionProps> = ({ section }) => {
    const { measurementId, measurement, files, filesLoading } = useMeasurementReport();

    const imageFiles = useMemo(
        () => files.filter((f) => (f.mimeType || '').startsWith('image/')),
        [files],
    );
    const otherFiles = useMemo(
        () => files.filter((f) => !(f.mimeType || '').startsWith('image/')),
        [files],
    );
    const sectionTitle = section?.title || 'Files';
    const sectionMeta = buildMeasurementSectionMeta(measurement);

    if (!measurementId) {
        return (
            <MeasurementSectionShell title={sectionTitle}>
                <Alert message="Select a measurement to view files." type="info" showIcon />
            </MeasurementSectionShell>
        );
    }

    if (filesLoading) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Spin tip="Loading files..." />
                </div>
            </MeasurementSectionShell>
        );
    }

    if (imageFiles.length === 0 && otherFiles.length === 0) {
        return (
            <MeasurementSectionShell title={sectionTitle} meta={sectionMeta}>
                <div className="report-measurement-panel-state">
                    <Empty description="No files attached" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            </MeasurementSectionShell>
        );
    }

    return (
        <MeasurementSectionShell
            title={sectionTitle}
            meta={sectionMeta}
            toolbar={
                <div className="report-measurement-summary">
                    {imageFiles.length > 0 ? <span className="report-measurement-pill">{imageFiles.length} images</span> : null}
                    {otherFiles.length > 0 ? <span className="report-measurement-pill report-measurement-pill--muted">{otherFiles.length} files</span> : null}
                </div>
            }
        >
            {imageFiles.length > 0 && (
                <div className="report-measurement-files-section">
                    <Text strong><FileImageOutlined /> Images</Text>
                    <div className="report-measurement-files-gallery">
                        <Image.PreviewGroup>
                            <Space size="middle" wrap>
                                {imageFiles.map((file) => (
                                    <div key={file.id} className="report-measurement-file-card">
                                        <Image
                                            src={file.signedUrl || ''}
                                            alt={file.fileName}
                                            className="report-measurement-file-thumb"
                                            preview={{ mask: 'Click to expand' }}
                                        />
                                        <Text type="secondary" className="report-measurement-file-name">
                                            {file.fileName}
                                        </Text>
                                    </div>
                                ))}
                            </Space>
                        </Image.PreviewGroup>
                    </div>
                </div>
            )}

            {otherFiles.length > 0 && (
                <div className="report-measurement-files-section">
                    <Text strong><FolderOutlined /> Other files</Text>
                    <List
                        size="small"
                        dataSource={otherFiles}
                        renderItem={(file) => {
                            const sizeLabel = formatFileSize(file.fileSize);
                            return (
                                <List.Item
                                    className="report-measurement-file-list-item"
                                    actions={file.signedUrl ? [
                                        <Button
                                            key="download"
                                            type="link"
                                            size="small"
                                            icon={<DownloadOutlined />}
                                            href={file.signedUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Download
                                        </Button>,
                                    ] : undefined}
                                >
                                    <Space direction="vertical" size={0}>
                                        <Text>{file.fileName}</Text>
                                        {sizeLabel ? <Text type="secondary">{sizeLabel}</Text> : null}
                                    </Space>
                                </List.Item>
                            );
                        }}
                        className="report-measurement-file-list"
                    />
                </div>
            )}
        </MeasurementSectionShell>
    );
};

export default ReportMeasurementFiles;
